import {
  TrendingUp, ShoppingCart, CloudSun, Sparkles, Package, Target,
  CalendarDays, Trash2, Plug, FileSpreadsheet, LineChart, Scale, Leaf,
} from "lucide-react";

export const NAV_ANCHORS = [
  { href: "#product", label: "Product" },
  { href: "#how-it-works", label: "How it works" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

export const MARQUEE_ITEMS = [
  "Daily Product-Level Forecasts",
  "Automated PO Generation",
  "Live OpenWeather Intelligence",
  "Lightspeed & Square Direct POS Sync",
  "Waste Tracking & Analytics",
  "Morning AI Briefings",
  "Central Multi-Site Ordering",
  "1-Click CSV/Excel Data Ingestion",
  "No Long-Term Lock-in",
];

export const PILLARS = [
  {
    icon: LineChart,
    title: "Product-level forecasts",
    body: "Daily demand forecasts for every product on your menu, built from your sales history, weather, holidays and local events.",
    large: true,
  },
  {
    icon: ShoppingCart,
    title: "Purchasing you can explain",
    body: "Order recommendations with the reasoning behind each one — accept, ignore, and generate purchase orders in 1 click.",
  },
  {
    icon: CloudSun,
    title: "Weather intelligence",
    body: "See how temperature, rain and wind shift your sales mix across the week ahead.",
    large: true,
  },
  {
    icon: Target,
    title: "Track forecast accuracy",
    body: "Predicted vs actuals with MAPE, so you always know how well the forecasts are performing.",
  },
  {
    icon: Leaf,
    title: "Waste, measured",
    body: "Log waste by product and reason, and see what it costs you over time.",
  },
];

export const MODULES = [
  { icon: TrendingUp, name: "Demand forecasting", desc: "Daily, product-level forecasts combining sales history, weather, holidays and events." },
  { icon: ShoppingCart, name: "Purchasing recommendations", desc: "What to order and how much, with reasoning shown — plus purchase order generation." },
  { icon: CloudSun, name: "Weather intelligence", desc: "Seven-day outlook with temperature and rain impact on your sales mix and revenue." },
  { icon: Sparkles, name: "AI insights", desc: "Plain-language morning briefings about your demand, stock, and kitchen prep." },
  { icon: Package, name: "Inventory & Recipes", desc: "Ingredient stock levels you can update in place, alongside your menu products." },
  { icon: Target, name: "Forecast accuracy", desc: "MAPE and predicted-vs-actual charts so performance is visible, not assumed." },
  { icon: CalendarDays, name: "Events & holidays", desc: "Add local events, holidays and promotions so forecasts account for them." },
  { icon: Trash2, name: "Waste tracking", desc: "Record waste by product and reason, with cost over time." },
  { icon: Plug, name: "POS integrations", desc: "Connect Lightspeed or Square to sync sales automatically, or import via CSV/Excel." },
  { icon: FileSpreadsheet, name: "CSV / Excel import", desc: "Import your sales history from CSV or Excel and start forecasting immediately." },
  { icon: Scale, name: "Multi-site oversight", desc: "Multiple locations with an organisation view and central purchasing." },
];

export const FAQS = [
  {
    q: "How fast can we get started?",
    a: "Under 5 minutes. Connect your Lightspeed Retail or Square account with one click (or upload a sales CSV/Excel spreadsheet), and Optics immediately analyzes your historical sales patterns and connects to local weather forecasts.",
  },
  {
    q: "Which POS systems are supported?",
    a: "Lightspeed Retail and Square are natively supported with automatic bi-directional sync every 15 minutes. We also support any POS via CSV/Excel drag-and-drop export. Additional integrations (Toast, Shopify POS, Vend) are in active development.",
  },
  {
    q: "How accurate are the daily demand forecasts?",
    a: "Forecasts are built from your own sales history, weather, holidays and events. Track predicted vs actual performance with MAPE in the Forecast Accuracy dashboard, so you can see exactly how well the model performs for your store.",
  },
  {
    q: "Do I need to enter credit card details for the free trial?",
    a: "Yes. A valid credit card is required to start the 14-day free trial. You will not be charged until the trial ends. Cancel anytime during the trial and you won't be billed.",
  },
  {
    q: "How does the automated purchasing work?",
    a: "Optics maps your predicted product demand against your recipes (e.g. 18g coffee beans + 220ml milk per flat white) and current on-hand stock. It then drafts purchase orders grouped by supplier with lead times factored in, so you never run out of milk or single-origin beans.",
  },
  {
    q: "Can Optics support multiple café locations?",
    a: "Yes. Our Multi-Site plan includes a centralized organizational dashboard, store switching, consolidated roastery/dairy supplier orders, and location-by-location accuracy metrics.",
  },
  {
    q: "Is our sales and pricing data secure?",
    a: "Yes. OAuth POS tokens are encrypted at rest, each organisation is isolated by tenant, and all traffic is served over TLS. We follow least-privilege access and never share your sales data with other customers.",
  },
];
